import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Loader2, Images, FileText, Eye } from 'lucide-react'
import { useProject } from '@/hooks/useProjects'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { AssetGrid } from '@/components/business/AssetGrid'
import { CopywritingWorkflow } from '@/components/business/CopywritingWorkflow'
import { XhsPreview } from '@/components/business/XhsPreview'
import { useNoteStyles } from '@/hooks/useNoteStyles'

export function ProjectPage() {
  const { id } = useParams<{ id: string }>()
  const projectId = id!
  const { project, isLoading } = useProject(projectId)
  const { styleMap } = useNoteStyles()
  
  // 控制主 Tab 切换（用于从文案生成跳转到预览导出）
  const [activeTab, setActiveTab] = useState('assets')
  
  // 文件夹选择状态，从 AssetGrid 提升到这里，以便上传素材时知道当前所在的分类
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  
  // 保存滚动位置，防止组件重新渲染时自动滚回顶部
  const scrollPositionRef = useRef<number>(0)
  const mainContentRef = useRef<HTMLDivElement>(null)
  
  // 监听滚动事件，保存滚动位置
  useEffect(() => {
    const mainElement = document.querySelector('main')
    if (!mainElement) return
    
    const handleScroll = () => {
      scrollPositionRef.current = mainElement.scrollTop
    }
    
    mainElement.addEventListener('scroll', handleScroll)
    return () => mainElement.removeEventListener('scroll', handleScroll)
  }, [])
  
  // 组件更新后恢复滚动位置
  useEffect(() => {
    const mainElement = document.querySelector('main')
    if (!mainElement || scrollPositionRef.current === 0) return
    
    // 使用 requestAnimationFrame 确保 DOM 已更新
    requestAnimationFrame(() => {
      mainElement.scrollTop = scrollPositionRef.current
    })
  })

  if (isLoading) {
    return (
      <div className="h-[50vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="h-[50vh] flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">项目不存在或已被删除</p>
        <Button asChild variant="outline">
          <Link to="/">返回首页</Link>
        </Button>
      </div>
    )
  }

  // 跳转到笔记库（预览导出 Tab）
  const handleNavigateToNotes = () => {
    setActiveTab('preview')
  }

  const currentStyle = project.note_style_id ? styleMap[project.note_style_id] : undefined

  return (
    <div ref={mainContentRef} className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon-sm" asChild className="-ml-1">
          <Link to="/">
            <ArrowLeft className="w-4 h-4" />
          </Link>
        </Button>
        <div className="space-y-0.5">
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight">{project.title}</h1>
            {currentStyle && (
              <Badge variant="outline" className="rounded-full px-2.5 py-0 text-[10px] h-5 font-normal bg-muted/30">
                {currentStyle.name}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {project.description || '无描述'}
          </p>
        </div>
      </div>

      {/* Main Content Tabs - 3个主Tab：素材库、文案生成、预览导出 */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex w-full p-1 bg-muted/50 rounded-xl h-10">
          <TabsTrigger value="assets" className="flex-1 gap-2 text-sm rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all h-8">
            <Images className="w-3.5 h-3.5" /> 素材库
          </TabsTrigger>
          <TabsTrigger value="copywriting" className="flex-1 gap-2 text-sm rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all h-8">
            <FileText className="w-3.5 h-3.5" /> 文案生成
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex-1 gap-2 text-sm rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all h-8">
            <Eye className="w-3.5 h-3.5" /> 笔记仓库
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assets" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <AssetGrid 
            projectId={project.id} 
            selectedFolderId={selectedFolderId}
            onSelectFolder={setSelectedFolderId}
          />
        </TabsContent>

        {/* Tab 2: 文案生成（重构后的分步视图） */}
        <TabsContent value="copywriting" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          <CopywritingWorkflow
            project={project}
            onNavigateToNotes={handleNavigateToNotes}
          />
        </TabsContent>

        {/* Tab 3: 预览导出（笔记库） */}
        <TabsContent value="preview" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <XhsPreview projectId={project.id} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
